import type { ErrorEvent } from '@sentry/nextjs';

/** Only a fully attributed external stack is safe to suppress. Message text,
 * anonymous frames, framework frames or a missing stack cannot prove origin. */
// Microsoft's link scanner (Outlook Safe Links) opens mailed links in a CefSharp
// browser whose .NET bridge rejects with exactly this text. It has no stack,
// our code never rejects with it, and only the full fixed format matches.
const CEFSHARP_BRIDGE_REJECTION = /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/;

export function isProvenExternalSentryNoise(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  if (values.length === 1 && values[0].mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
    && CEFSHARP_BRIDGE_REJECTION.test(String(values[0].value || ''))) return true;
  return values.every(value => {
    const frames = value.stacktrace?.frames;
    // An evaluated helper named window.__visible (injected by an automation or
    // visibility tool, never present in our bundles) leaves only <anonymous>
    // frames. Suppress exactly that signature, not anonymous stacks in general.
    if (frames?.length && frames.every(frame => frame.in_app !== true && frame.filename === '<anonymous>')
      && frames.some(frame => frame.function === 'window.__visible')) return true;
    return Boolean(frames?.length) && frames!.every(frame => {
      if (frame.in_app === true || !frame.filename) return false;
      const filename = frame.filename;
      if (/^(chrome-extension|moz-extension|safari-web-extension):\/\//i.test(filename)) return true;
      // These exact native-injected script names are evidenced by the existing
      // browser bridge incidents. Never suppress app:///_next or a generic URL.
      if (/^app:\/\/[^?#]*\/navigation_performance_logger(?:_android)?(?:\.js)?(?:[?#]|$)/i.test(filename)
        && !filename.includes('/_next/')) return true;
      return /^https:\/\/apis\.google\.com\/js\//i.test(filename);
    });
  });
}
